// Review side panel: content checks (click to jump to the unit), comments,
// and the lifecycle action for the selected version's state. Without required
// review a draft publishes directly; with it: submit (draft), approve /
// request changes (in review), publish with a rollout % (approved).

import { useState } from "react";
import {
  approveVersion,
  errorChecks,
  errorText,
  publishVersion,
  requestChanges,
  submitVersion,
} from "../../services/talkTracks";
import type {
  ContentCheck,
  TalkTrackComment,
  TalkTrackMeta,
  TalkTrackTopic,
  TalkTrackVersion,
} from "../../types/talkTrack";
import { CommentItem, CommentThread } from "./Comments";

interface Props {
  meta: TalkTrackMeta | null;
  // /meta failed: lifecycle actions stay hidden (review rules unknown).
  metaFailed?: boolean;
  topic: TalkTrackTopic;
  version: TalkTrackVersion;
  checks: ContentCheck[];
  comments: TalkTrackComment[];
  dirty: boolean;
  // null while the simulation list is still loading.
  hasPassingSimulation: boolean | null;
  onFocusUnit: (unitId: string) => void;
  onComment: (comment: TalkTrackComment) => void;
  // Blocking checks returned by a rejected submit/approve/publish.
  onChecks: (checks: ContentCheck[]) => void;
  // Called after a successful transition; the page reloads topic + version.
  onDone: (message: string) => Promise<void> | void;
  onError: (message: string) => void;
}

export function ReviewPanel({
  meta, metaFailed = false, topic, version, checks, comments, dirty, hasPassingSimulation,
  onFocusUnit, onComment, onChecks, onDone, onError,
}: Props) {
  const [note, setNote] = useState("");
  const [rolloutPct, setRolloutPct] = useState(topic.liveVersion !== null ? 20 : 100);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const errors = checks.filter((c) => c.level === "error");
  const warnings = checks.filter((c) => c.level === "warning");
  const openComments = comments.filter((c) => !c.resolved);
  const openUnitComments = openComments.filter((c) => c.unitId);
  const generalComments = comments.filter((c) => !c.unitId);

  const isAuthor = !!version.authorUid && version.authorUid === meta?.uid;
  const reviewBlocked = !meta
    ? "Checking your reviewer permissions…"
    : !meta.isReviewer
      ? "You're not on the talk-track reviewer list."
      : isAuthor && !meta.allowSelfApproval
        ? "You authored this version — someone else has to review it."
        : null;

  async function run(label: string, action: () => Promise<unknown>, success: string) {
    setBusy(true);
    setError(null);
    try {
      await action();
      setNote("");
      await onDone(success);
    } catch (e) {
      const blocking = errorChecks(e);
      if (blocking) onChecks(blocking);
      setError(`${label} failed: ${errorText(e)}`);
    } finally {
      setBusy(false);
    }
  }

  function publish() {
    const pct = topic.liveVersion === null ? 100 : rolloutPct;
    const what =
      pct >= 100
        ? `Publish v${version.version} to everyone?${topic.liveVersion !== null ? ` v${topic.liveVersion} retires.` : ""}`
        : `Publish v${version.version} as the candidate for ${pct}% of users? The rest stay on v${topic.liveVersion}.`;
    if (!confirm(what)) return;
    run("Publish", () => publishVersion(topic.id, version.version, pct),
      pct >= 100 ? `v${version.version} is live ✓` : `v${version.version} is the candidate at ${pct}% ✓`);
  }

  const simReason =
    meta?.requireSimulation && hasPassingSimulation === false
      ? "Needs a passing simulation run first (see Simulation below)."
      : null;

  // Rollout choice + Publish button, shared by direct publish and approved.
  function publishControls(blocked: string | null) {
    const reason = blocked ?? simReason;
    return (
      <>
        {topic.liveVersion !== null ? (
          <>
            <label htmlFor="tt-publish-pct">Rollout % (100 replaces live v{topic.liveVersion})</label>
            <input id="tt-publish-pct" type="number" min={1} max={100} value={rolloutPct}
              onChange={(e) => setRolloutPct(Math.min(100, Math.max(1, parseInt(e.target.value || "1", 10))))} />
            <div className="muted tt-small">
              Below 100% starts an A/B test: this share of users gets v{version.version}, the rest stay on live.
            </div>
          </>
        ) : (
          <div className="muted tt-small">Nothing is live yet, so this version goes live for everyone.</div>
        )}
        <button className="btn btn-good" disabled={busy || !!reason} onClick={publish}>
          Publish
        </button>
        {reason && <div className="tt-reason">{reason}</div>}
      </>
    );
  }

  const draftReason = dirty
    ? "Save your changes first."
    : errors.length
      ? `Fix ${errors.length} blocking error${errors.length === 1 ? "" : "s"} first.`
      : null;
  const directPublish = meta !== null && !meta.requireReview;
  let action: React.ReactNode = null;
  if (meta === null && version.status !== "published" && version.status !== "retired") {
    // Until meta says whether review is required, show no lifecycle button:
    // the review-flow buttons flashed here and a quick click submitted.
    action = (
      <div className="muted tt-small">
        {metaFailed ? "Talk-track settings didn't load — reload the page to publish." : "Loading…"}
      </div>
    );
  } else if (directPublish && (version.status === "draft" || version.status === "in_review" || version.status === "approved")) {
    action = (
      <>
        {openComments.length > 0 && (
          <div className="muted tt-small">
            {openComments.length} open comment{openComments.length === 1 ? "" : "s"} below — worth a read before publishing.
          </div>
        )}
        {publishControls(draftReason)}
      </>
    );
  } else if (version.status === "draft") {
    action = (
      <>
        {version.reviewNote && (
          <div className="callout tt-callout">
            Changes requested{version.reviewerUid ? <> by <span className="mono">{version.reviewerUid}</span></> : ""}:{" "}
            {version.reviewNote}
          </div>
        )}
        <button className="btn btn-primary" disabled={busy || !!draftReason}
          onClick={() => run("Submit", () => submitVersion(topic.id, version.version), `v${version.version} submitted for review ✓`)}>
          Submit for review
        </button>
        {draftReason && <div className="tt-reason">{draftReason}</div>}
      </>
    );
  } else if (version.status === "in_review") {
    const approveReason =
      reviewBlocked ??
      (errors.length
        ? `${errors.length} blocking error${errors.length === 1 ? "" : "s"} must be fixed (request changes).`
        : openComments.length
          ? `Resolve the ${openComments.length} open comment${openComments.length === 1 ? "" : "s"} before approving.`
          : null);
    action = (
      <>
        <label htmlFor="tt-review-note">Review note (optional)</label>
        <textarea id="tt-review-note" rows={2} value={note} disabled={!!reviewBlocked}
          placeholder="What you checked, or what needs to change…" onChange={(e) => setNote(e.target.value)} />
        <div className="tt-actions">
          <button className="btn btn-good" disabled={busy || !!approveReason}
            onClick={() => run("Approve", () => approveVersion(topic.id, version.version, note), `v${version.version} approved ✓`)}>
            Approve
          </button>
          <button className="btn" disabled={busy || !!reviewBlocked}
            onClick={() => run("Request changes", () => requestChanges(topic.id, version.version, note),
              `Changes requested on v${version.version} — it's a draft again`)}>
            Request changes
          </button>
        </div>
        {approveReason && <div className="tt-reason">{approveReason}</div>}
      </>
    );
  } else if (version.status === "approved") {
    action = (
      <>
        {version.reviewerUid && (
          <div className="muted tt-small">
            Approved by <span className="mono">{version.reviewerUid}</span>
            {version.reviewNote ? ` — “${version.reviewNote}”` : ""}
          </div>
        )}
        {publishControls(null)}
      </>
    );
  } else if (version.status === "published") {
    action = (
      <div className="muted tt-small">
        {topic.liveVersion === version.version
          ? "This version is live."
          : topic.candidateVersion === version.version
            ? `This version is the candidate at ${topic.rolloutPct}% — manage it under Rollout.`
            : "Published."}
      </div>
    );
  } else {
    action = <div className="muted tt-small">Retired. Restore it from the History tab to make it live again.</div>;
  }

  return (
    <div className="tt-side-section">
      <div className="panel-head">Review · v{version.version}</div>
      <div className="tt-action-box">
        {action}
        {error && <div className="error-text">{error}</div>}
      </div>

      <div className="tt-side-sub">
        Checks{" "}
        <span className={errors.length ? "tt-count error" : "tt-count"}>{errors.length} error{errors.length === 1 ? "" : "s"}</span>
        {" · "}
        <span className={warnings.length ? "tt-count warning" : "tt-count"}>{warnings.length} warning{warnings.length === 1 ? "" : "s"}</span>
      </div>
      {dirty && <div className="muted tt-small">Checks reflect the last save.</div>}
      {checks.length === 0 ? (
        <div className="muted tt-small">All checks pass.</div>
      ) : (
        <ul className="tt-checks">
          {[...errors, ...warnings].map((c, i) => (
            <li key={`${c.code}-${c.unitId}-${i}`}>
              <button className={`tt-check ${c.level}`} disabled={!c.unitId}
                onClick={() => c.unitId && onFocusUnit(c.unitId)}
                title={c.unitId ? `Go to ${c.unitId}` : undefined}>
                <span className="tt-check-level">{c.level}</span>
                <span className="tt-check-msg">
                  {c.message}
                  {c.unitId && <span className="mono tt-check-unit">{c.unitId}</span>}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="tt-side-sub">
        Comments{" "}
        <span className={openComments.length ? "tt-count warning" : "tt-count"}>{openComments.length} open</span>
      </div>
      {openUnitComments.length > 0 && (
        <div className="tt-thread">
          {openUnitComments.map((c) => (
            <CommentItem key={c.id} comment={c} onChange={onComment} onError={onError} onFocusUnit={onFocusUnit} />
          ))}
        </div>
      )}
      <details className="tt-details">
        <summary>General comments ({generalComments.length})</summary>
        <CommentThread topicId={topic.id} version={version.version} unitId={null}
          comments={generalComments} onComment={onComment} onError={onError} />
      </details>
    </div>
  );
}
